import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SkeletonCard } from "@/components/Skeleton";
import { BellRing, ShoppingBag, UserPlus, XCircle, ShieldAlert, AlertOctagon } from "lucide-react";

const TOGGLES = [
    {
        key: "admin_email_new_pending",
        icon: UserPlus,
        title: "Nouvelle demande d'accès",
        description: "Un professionnel s'inscrit et attend votre validation.",
    },
    {
        key: "admin_email_new_order",
        icon: ShoppingBag,
        title: "Nouvelle commande",
        description: "Un client valide une nouvelle commande dans l'Espace Pro.",
    },
    {
        key: "admin_email_order_cancelled",
        icon: XCircle,
        title: "Commande annulée par le client",
        description: "Le client annule sa commande dans le délai de 15 minutes.",
    },
    {
        key: "admin_email_profile_change",
        icon: ShieldAlert,
        title: "Modification IDE/TVA",
        description: "Un champ sensible (IDE/TVA) est modifié par un client pro.",
    },
    {
        key: "admin_email_quality_complaint",
        icon: AlertOctagon,
        title: "Réclamation qualité",
        description: "Un client signale un problème qualité sur une commande livrée.",
    },
];

function ToggleRow({ icon: Icon, title, description, value, onChange, testId, disabled }) {
    return (
        <div className="bc-notif-row" data-testid={`notif-row-${testId}`}>
            <div className="bc-notif-row__icon">
                <Icon size={18} />
            </div>
            <div className="bc-notif-row__body">
                <p className="bc-notif-row__title">{title}</p>
                <p className="bc-notif-row__desc">{description}</p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={value}
                onClick={() => onChange(!value)}
                disabled={disabled}
                className={`bc-toggle ${value ? "bc-toggle--on" : ""}`}
                data-testid={`notif-toggle-${testId}`}
            >
                <span className="bc-toggle__knob" />
            </button>
        </div>
    );
}

export default function AdminNotifications() {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ["admin-email-preferences"],
        queryFn: async () => (await api.get("/admin/email-preferences")).data,
    });

    const [prefs, setPrefs] = useState(null);

    useEffect(() => {
        if (data) setPrefs(data);
    }, [data]);

    const save = useMutation({
        mutationFn: (payload) => api.patch("/admin/email-preferences", payload),
        onMutate: () => ({ previous: prefs }),
        onSuccess: () => {
            toast.success("Préférences de notifications enregistrées");
            qc.invalidateQueries({ queryKey: ["admin-email-preferences"] });
        },
        onError: (e, _vars, ctx) => {
            // Roll back optimistic UI update
            if (ctx?.previous) setPrefs(ctx.previous);
            toast.error(formatApiError(e));
        },
    });

    const handleToggle = (key, value) => {
        const next = { ...prefs, [key]: value };
        setPrefs(next);
        save.mutate(next);
    };

    const setAll = (value) => {
        const next = TOGGLES.reduce((acc, t) => ({ ...acc, [t.key]: value }), {});
        setPrefs(next);
        save.mutate(next);
    };

    if (isLoading || !prefs) {
        return (
            <div data-testid="admin-notifications-loading" className="space-y-3">
                <SkeletonCard lines={3} />
                <SkeletonCard lines={3} />
                <SkeletonCard lines={3} />
            </div>
        );
    }

    const allOn = TOGGLES.every((t) => prefs[t.key]);
    const allOff = TOGGLES.every((t) => !prefs[t.key]);

    return (
        <div className="bc-anim-in" data-testid="admin-notifications">
            <div className="flex items-baseline justify-between mb-2">
                <h2 className="font-serif text-2xl text-[#D4BC7C]">Notifications e-mail</h2>
                <BellRing size={18} className="text-[#D4BC7C]/70" />
            </div>
            <p className="text-sm text-[#F2ECE0]/70 mb-6 max-w-2xl leading-relaxed">
                Choisissez les événements qui déclenchent l&apos;envoi d&apos;un e-mail à l&apos;adresse administrateur.
                Les commandes et demandes restent visibles dans l&apos;admin même si l&apos;e-mail est désactivé.
            </p>

            <div className="flex gap-3 mb-8">
                <button
                    type="button"
                    onClick={() => setAll(true)}
                    disabled={allOn || save.isPending}
                    className="bc-btn-ghost text-xs uppercase tracking-widest px-4 py-2 border border-[#D4BC7C]/40 hover:border-[#D4BC7C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="notif-enable-all"
                >
                    Tout activer
                </button>
                <button
                    type="button"
                    onClick={() => setAll(false)}
                    disabled={allOff || save.isPending}
                    className="bc-btn-ghost text-xs uppercase tracking-widest px-4 py-2 border border-[#F2ECE0]/20 hover:border-[#F2ECE0]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="notif-disable-all"
                >
                    Tout désactiver
                </button>
            </div>

            <div className="space-y-2">
                {TOGGLES.map((t) => (
                    <ToggleRow
                        key={t.key}
                        icon={t.icon}
                        title={t.title}
                        description={t.description}
                        value={!!prefs[t.key]}
                        onChange={(v) => handleToggle(t.key, v)}
                        testId={t.key.replace("admin_email_", "")}
                        disabled={save.isPending}
                    />
                ))}
            </div>

            <p className="mt-8 text-xs text-[#7a7a7a] leading-relaxed max-w-2xl">
                Astuce&nbsp;: si vous partez en congés, vous pouvez désactiver temporairement les alertes de commandes
                pour préserver votre boîte mail — les commandes seront toujours enregistrées dans l&apos;admin.
            </p>
        </div>
    );
}
